import * as React from "react";
import {
    useNavigate
} from "react-router-dom";
import {
    apiEndpoint,
    LOGIN_ERROR_DICT
} from "config/config";
import useLocalStorage from "hooks/useLocalStorage";
import ISession from "models/ISession";
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from "@mui/material/Backdrop";
import { Base64 } from 'js-base64';
import useQueryParam from "hooks/useQueryParam";




interface Err {
    error: true
    code: string
    message: string
    // token for restoring signup process after importing missed
    // Apple organization
    restoration_token?: string
}
type Ok<T> = { error: false } & T;
type Result<T> = Ok<T> | Err;

export interface SigninMethods {
    withAccessToken: (authToken: string) => Promise<Result<ISession>>;
    withSessionData: (sessionData: ISession) => Promise<Result<ISession>>;
    withAppleCode: (code: string) => Promise<Result<ISession>>;
    withApple: (redirectPath?: string) => void;
}

export interface AuthContextType {
    session: ISession | null;
    setSession: (value: ISession | null | ((val: ISession | null) => ISession | null)) => void;
    signin: SigninMethods;
    signout: () => Promise<void>;
    error: string;
    setError: React.Dispatch<React.SetStateAction<string>>;
    loading: boolean;
}



const AuthContext = React.createContext<AuthContextType>(null!);

export default function AuthProvider({ children }: { children: React.ReactNode }) {
    // const { session_token, setSessionToken, deleteSessionToken } = useToken();
    const [session, setSession] = useLocalStorage<ISession|null>('session', null);
    const [error, setError] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const process = (func: (...args: any[]) => Promise<Result<ISession>>) => {
        return async (...args: any[]) => {
            setLoading(true);
            let result = await func(...args);
            if (result.error) {
                let msg = formatErrorMessage(result.message);
                setError(msg);
            } else {
                setSession(result as ISession);
            }
            setLoading(false);
            return result;
        };
    }
    
    const signin = {
        withAccessToken: process(async (authToken: string) => {
            return await GET<ISession>('/signin_with_access_token', {access_token: authToken});
        }),
        withSessionData: process(async (sessionData: ISession) => {
            return { error: false, ...sessionData };
        }),
        withAppleCode: process(async (code: string) => {
            const result = await GET<ISession>('/auth/signup_with_apple', {code});
            if (result.error && result.message === "ORGANIZATION_NOT_REGISTERED") {
                // break navigation logic here, want to be sure that we are going to
                // register Apple organization, so we can restore signup process
                // after importing missed Apple organization
                window.location.href = "/apple?restoration_token=" + result.restoration_token
            }
            return result;
        }),
        withApple: (redirectPath: string = "") => {
            const params = {
                "response_type": "code",
                "response_mode": "query",
                "client_id": "ltd.funexpected.teachers",
                "redirect_uri": "https://api.funexpected.cloud/auth/apple/redirect",
                "state": buildAppleCallbackState(redirectPath)
            }
            const queryString = new URLSearchParams(params).toString();
            const url = `https://appleid.apple.com/auth/authorize?${queryString}`;
            window.location.href = url;
        },
    };
    
    const signout = async () => {
        setSession(null);
        window.location.href = "/";
    };

    const value = { session, setSession, signin, signout, error, setError, loading };
    return (
        <AuthContext.Provider value={value}>
            <Backdrop open={loading} sx={{ zIndex: 2000, backdropFilter: 'brightness(80%)', backgroundColor: 'transparent' }}>
                <CircularProgress color="primary" />
            </Backdrop>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return React.useContext(AuthContext);
}

export function useSession(): ISession {
    const { session } = useAuth();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (!session) {
            navigate("/", { replace: true });
        }
    }, [session, navigate]);

    return session as ISession;
}

export function RequireAuth({ children }: { children: JSX.Element }) {
    const auth = useAuth();
    const navigate = useNavigate();
    const access_token = useQueryParam('access_token');
    const encoded_session_data = useQueryParam('session_data');
    const code = useQueryParam('code');
    // const state = useQueryParam('state');
    
    React.useEffect(()=>{
        async function update() {
            if (access_token) {
                await auth.signin.withAccessToken(access_token);
            } else if (encoded_session_data) {
                const session_data = JSON.parse(Base64.decode(encoded_session_data)) as ISession;
                await auth.signin.withSessionData(session_data)
            } else if (code) {
                await auth.signin.withAppleCode(code);
            }
            if (!auth.session && !auth.loading && !access_token && !encoded_session_data) {
                navigate("/signin", {replace: true});
            }
        }
        update();
    }, [auth, access_token, encoded_session_data, code, navigate]);
    if (auth.session) {
        return children;
    } else {
        // Show loading state instead of blank screen while authenticating
        return <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh'}}>
            <div>Loading...</div>
        </div>;
    }
}

function GET<T>(path: string, params: Record<string, any>): Promise<Result<T>> {
    const queryString = Object.entries(params)
        .map(([key, value]) => encodeURIComponent(key) + '=' + encodeURIComponent(value))
        .join('&');
    return fetch(`${apiEndpoint}${path}?${queryString}`)
        .then(data => data.json())
        .then(data => {
            if ((data as Err).code && (data as Err).message) {
                return { error: true, ...data } as Err;
            } else {
                return { error: false, ...data } as Ok<T>;
            }
        });
}

// function POST<T>(path: string, params: Record<string, any>): Promise<Result<T>> {
//     return fetch(`${apiEndpoint}${path}`, {
//         method: 'POST',
//         headers: {
//             'Content-Type': 'application/json'
//         },
//         body: JSON.stringify(params)
//     })
//         .then(data => data.json())
//         .then(data => {
//             if ((data as Err).code && (data as Err).message) {
//                 return { error: true, ...data } as Err;
//             } else {
//                 return { error: false, ...data } as Ok<T>;
//             }
//         });
// }

function formatErrorMessage(message: string): string {
    if (message.startsWith("TOO_MANY_ATTEMPTS_TRY_LATER")) {
        message = "TOO_MANY_ATTEMPTS_TRY_LATER";
    }
    if (message in LOGIN_ERROR_DICT) {
        return LOGIN_ERROR_DICT[message as keyof typeof LOGIN_ERROR_DICT];
    } else {
        return message;
    }
}

function buildAppleCallbackState(path:string = "", state:Record<string, any> = {}) {
    const l = window.location;
    if (!path.startsWith("/")) {
        path = "/" + path
    }
    const origin = `${l.protocol}//${l.host}${path}`
    state = { origin, ...state };
    return Base64.encode(JSON.stringify({origin, ...state}))
}
